import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { Operators } from '@rivethealth/data/advanced-filters';
import { Option } from '@rivethealth/data/options';
import {
  Filter,
  friendlyMultiSelectOperator,
  Operator,
} from '@rivethealth/data/super-table';
import * as _ from 'lodash';
import { ObservableInput } from 'observable-input';
import { BehaviorSubject, combineLatest, merge, Observable } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  startWith,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { Loadable } from '../helpers/loadable';
import { OptionsLoader } from '../input/option-select.component';
import { FiltersComponent } from './filters.component';

@Component({
  selector: 'advanced-filter-tree-multi-select',
  templateUrl: './advanced-filter-tree-multi-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdvancedFilterTreeMultiSelectComponent<Name extends string> {
  constructor(
    private readonly filters: FiltersComponent<{
      [K in Name]: Filter<string[]>;
    }>,
  ) {}

  @Input()
  @ObservableInput()
  generalize!: Observable<(ids: string[]) => string[]>;

  @Input()
  @ObservableInput()
  resolve!: Observable<(ids: string[]) => string[]>;

  @ObservableInput()
  @Input()
  name!: Observable<Name>;

  @ObservableInput()
  @Input('multiValued')
  _multiValued!: Observable<boolean>;
  readonly multiValued = this._multiValued.pipe(startWith(false));

  @ObservableInput()
  @Input('optional')
  _optional!: Observable<boolean>;
  readonly optional = this._optional.pipe(startWith(false));

  @ObservableInput()
  @Input('options')
  _options!: Observable<Option[]>;
  readonly options = this._options.pipe(startWith([]));

  @ObservableInput()
  @Input()
  optionsLoadable!: Observable<Loadable<{ options: Option[]; count: number }>>;

  @ObservableInput()
  @Input()
  optionsLoader!: Observable<OptionsLoader>;

  @Input()
  optionsFilterable: boolean;

  @ObservableInput()
  @Input('operators')
  _operators!: Observable<Operator[]>;

  readonly defaultOperators: Observable<Operator[]> = combineLatest([
    this.multiValued,
    this.optional,
  ]).pipe(
    map(([multiValued, optional]) => {
      return multiValued
        ? optional
          ? [
              'ANY_EQ',
              'NOT_ANY_EQ',
              'ALL_EQ',
              'NOT_ALL_EQ',
              'EMPTY',
              'NOT_EMPTY',
            ]
          : ['ANY_EQ', 'NOT_ANY_EQ', 'ALL_EQ', 'NOT_ALL_EQ']
        : optional
          ? ['ANY_EQ', 'NOT_ANY_EQ', 'EMPTY', 'NOT_EMPTY']
          : ['ANY_EQ', 'NOT_ANY_EQ'];
    }),
  );
  readonly operators: Observable<Operator[]> = merge(
    this._operators,
    this.defaultOperators,
  );

  readonly operatorOptions: Observable<Option[]> = this.operators.pipe(
    map(operators => {
      return operators.map(o => ({
        title: friendlyMultiSelectOperator(o),
        subtitle: null,
        id: o,
      }));
    }),
  );

  @Input()
  placeholder: string = '';

  readonly selectedOperatorChange = new BehaviorSubject<Operator | null>(null);
  readonly selectedOptionIdsChange = new BehaviorSubject<string[] | null>(null);

  readonly filterIds = combineLatest([
    this.filters.value,
    this.name,
    this.resolve.pipe(
      startWith((a: string[]) => a),
      filter(a => a != null),
    ),
  ]).pipe(
    map(([value, name, resolve]) => resolve(value[name].filter!)),
    distinctUntilChanged((ids1, ids2) =>
      _.isEqual(new Set(ids1), new Set(ids2)),
    ),
  );

  readonly selectedOperator = combineLatest([
    this.filters.value,
    this.name,
  ]).pipe(map(([value, name]) => value[name].operator));

  readonly showDropdown = this.selectedOperator.pipe(
    map(operator => Operators.arity(operator) !== 'NULLARY'),
  );

  filterChangeHandler = combineLatest([
    this.selectedOperatorChange.pipe(
      map(operatorChange => {
        if (operatorChange === null) return null;
        // Clear out the Subject so we know it hasn't changed next time
        this.selectedOperatorChange.next(null);
        return operatorChange;
      }),
    ),
    this.selectedOptionIdsChange.pipe(
      map(optionIds => {
        if (optionIds === null) return null;
        // Clear out the Subject so we know it hasn't changed next time
        this.selectedOptionIdsChange.next(null);
        return optionIds;
      }),
    ),
  ]).pipe(
    filter(changes => changes.some(change => change !== null)),
    withLatestFrom(
      this.selectedOperator,
      this.filterIds,
      this.generalize.pipe(
        startWith((a: string[]) => a),
        filter(a => a != null),
      ),
    ),
    map(
      ([
        [operatorChange, optionIdsChange],
        operator,
        optionIds,
        generalize,
      ]) => {
        return {
          operator: operatorChange ?? operator,
          filter: generalize(optionIdsChange ?? optionIds),
        };
      },
    ),
    withLatestFrom(this.name),
    tap(([filter, name]) => {
      this.filters.changeValue.next({ name, reduce: () => filter });
    }),
  );
}
